import { useEffect, useMemo, useState } from "react";
import FormularioButom from "./FormularioButom";
import FormularioInput from "./FormularioInput";
import FormularioList from "./FormularioList";
import * as Yup from "yup";
import "./Formulario.css";
import { useNavigate, useParams } from "react-router-dom";
import Api from "../Services/Api"; 

// --- helpers ---
const onlyDigits = (v = "") => String(v).replace(/\D/g, "");

const parseMoneyBR = (value) => {
  if (value === null || value === undefined) return 0;

  // aceita: "1.234,56" | "1234,56" | "1234.56" | 1234.56
  const str = String(value).trim();
  if (!str) return 0;

  // remove tudo que não for dígito, ponto ou vírgula
  const cleaned = str.replace(/[^\d.,-]/g, "");

  // se tiver vírgula, assume BR (vírgula decimal) e remove pontos de milhar
  if (cleaned.includes(",")) {
    const normalized = cleaned.replace(/\./g, "").replace(",", ".");
    const n = Number(normalized);
    return Number.isFinite(n) ? n : 0;
  }

  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const formatMoneyBR = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "0,00";
  return v.toFixed(2).replace(".", ",");
};


const isCpfOrCnpjValid = (value) => {
  const v = onlyDigits(value);
  return v.length === 11 || v.length === 14;
};

const buildSchema = (isEdit) =>
  Yup.object({
    nome: Yup.string()
      .trim()
      .min(3, "Informe pelo menos 3 caracteres.")
      .required("Nome / Razão social é obrigatório."),
    acesso: Yup.mixed()
      .oneOf(["LICENSED", "ADMIN"], "Selecione um acesso válido.")
      .required("Acesso é obrigatório."),
    email: Yup.string()
      .trim()
      .email("E-mail inválido.")
      .required("E-mail é obrigatório.")
      .test(
        "dominio-smartolfativos",
        "O e-mail deve ser do domínio @smartolfativos.com.br",
        (value) => !!value && value.toLowerCase().endsWith("@smartolfativos.com.br")
      ),
    cpfCnpj: Yup.string()
      .required("CPF ou CNPJ é obrigatório.")
      .test("cpf-cnpj", "Informe um CPF (11) ou CNPJ (14) válido.", isCpfOrCnpjValid),
    telefone: Yup.string()
      .required("Telefone é obrigatório.")
      .test("phone", "Telefone deve ter DDD + número (10 ou 11 dígitos).", (v) => {
        const d = onlyDigits(v);
        return d.length === 10 || d.length === 11;
      }),
    status: Yup.mixed()
      .oneOf(["ACTIVE", "INACTIVE"], "Selecione um status válido.")
      .required("Status é obrigatório."),
    // ✅ senha: obrigatória só no cadastro; na edição, valida somente se o usuário preencher
    senha: isEdit
      ? Yup.string()
          .transform((v) => (v ? v : ""))
          .test("min-if-filled", "A senha deve ter no mínimo 6 caracteres.", (v) => {
            if (!v) return true; // vazio = ok (não altera senha)
            return v.length >= 6;
          })
      : Yup.string()
          .required("Senha é obrigatória.")
          .min(6, "A senha deve ter no mínimo 6 caracteres."),
    withdrawLimit: Yup.number()
      .typeError("Informe um número válido para o limite.")
      .min(0, "O limite não pode ser negativo.")
      .required("Limite é obrigatório."),
  });
  

const FormularioAddUsuarios = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [cpfCnpj, setCpfCnpj] = useState("");
  const [telefone, setTelefone] = useState("");
  const [acesso, setAcesso] = useState("");
  const [status, setStatus] = useState("");
  const [senha, setSenha] = useState("");
  const [withdrawLimit, setWithdrawLimit] = useState("0,00");

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const schema = useMemo(() => buildSchema(isEdit), [isEdit]);


  // ✅ Carrega dados no modo edição
  useEffect(() => {
    const carregar = async () => {
      if (!isEdit) return;
      try {
        setLoading(true);
        setErrors({});

        const res = await Api.get(`/usuarios/${id}`);
        const u = res.data;

        setNome(u.full_name ?? "");
        setEmail(u.email ?? "");
        setCpfCnpj(u.cpf_cnpj ?? "");
        setTelefone(u.phone ?? "");
        setAcesso(u.access_type ?? "");
        setStatus(u.status ?? "");
        setWithdrawLimit(formatMoneyBR(u.withdraw_limit ?? 0));
        setSenha(""); // sempre vazio: só altera se preencher
      } catch (err) {
        console.log("ERRO ao carregar usuário:", err.response?.data || err.message);
      } finally {
        setLoading(false);
      }
    };

    carregar();
  }, [id, isEdit]);

  const aoSalvar = async (evento) => {
    evento.preventDefault();

    try {
      setErrors({});
      await schema.validate(
        { nome, email, cpfCnpj, telefone, acesso, status, senha, withdrawLimit: parseMoneyBR(withdrawLimit) },
        { abortEarly: false }
      );
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const fieldErrors = {};
        err.inner.forEach((e) => {
          if (e.path) fieldErrors[e.path] = e.message;
        });
        setErrors(fieldErrors);
        return;
      }
    }

    try {
      setLoading(true);

      // payload base (cadastro e edição)
      const payload = {
        full_name: nome.trim(),
        email: email.trim(),
        cpf_cnpj: onlyDigits(cpfCnpj),
        phone: onlyDigits(telefone),
        access_type: acesso,
        status: status,
        withdraw_limit: parseMoneyBR(withdrawLimit),
      };


      // ✅ só envia senha se tiver preenchido (edição) | no cadastro já vem preenchida
      if (!isEdit || (isEdit && senha.trim())) {
        payload.password_hash = senha;
      }

      if (isEdit) {
        await Api.put(`/usuarios/${id}`, payload);
      } else {
        await Api.post("/cadastrousuarios", payload);
      }

      navigate("/usuarios");
    } catch (err) {
      console.log("ERRO ao salvar:", err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  const listaStatus = [
    { label: "Ativo", value: "ACTIVE" },
    { label: "Inativo", value: "INACTIVE" },
  ];

  const listaAcesso = [
    { label: "Usuário", value: "LICENSED" },
    { label: "Administrador", value: "ADMIN" },
  ];

  const cpfCnpjMask = [{ mask: "000.000.000-00" }, { mask: "00.000.000/0000-00" }];
  const cpfCnpjDispatch = (appended, masked) => {
    const next = onlyDigits((masked.value || "") + appended);
    return next.length > 11 ? masked.compiledMasks[1] : masked.compiledMasks[0];
  };

  return (
    <form id="bank-form" noValidate onSubmit={aoSalvar}>
      {loading && <p>Carregando...</p>}

      <FormularioInput
        type="text"
        size="full"
        title="Nome / Razão social"
        required="required"
        id="nomeLicenciado"
        placeholder="Ex: João Silva, Clínica Bem-Estar..."
        helper="Nome que aparecerá no extrato e na área do licenciado."
        valor={nome}
        aoAlterado={(valor) => setNome(valor)}
        error={errors.nome}
        readOnly={isEdit}   // ✅ trava só quando estiver editando
      />

      <FormularioList
        itens={listaAcesso}
        title="Acesso"
        required="required"
        id="acessoLicenciado"
        placeholder="Status"
        helper="Usuarios não conseguem acessar o painel administrador."
        lista="listaAcesso"
        valor={acesso}
        aoAlterado={(valor) => setAcesso(valor)}
        error={errors.acesso}
      />

      <FormularioInput
        type="email"
        title="E-mail de acesso"
        required="required"
        id="emailLicenciado"
        placeholder="nome@smartolfativos.com.br"
        helper="E-mail único que será utilizado no login."
        valor={email}
        aoAlterado={(valor) => setEmail(valor)}
        error={errors.email}
      />

      <FormularioInput
        type="text"
        title="CPF ou CNPJ"
        required="required"
        id="cpfCnpj"
        placeholder="000.000.000-00"
        valor={cpfCnpj}
        aoAlterado={(v) => setCpfCnpj(v)}
        error={errors.cpfCnpj}
        helper="Documento utilizado para notas fiscais e pagamentos."
        mask={cpfCnpjMask}
        dispatch={cpfCnpjDispatch}
      />

      <FormularioInput
        type="text"
        title="WhatsApp"
        required="required"
        id="telefoneLicenciado"
        placeholder="(00) 00000-0000"
        valor={telefone}
        aoAlterado={(v) => setTelefone(v)}
        error={errors.telefone}
        helper="Contato direto com o licenciado."
        mask="(00) 00000-0000"
      />

      <FormularioList
        itens={listaStatus}
        title="Status"
        required="required"
        id="statusLicenciado"
        placeholder="Status"
        helper="Licenciados inativos não conseguem acessar o painel."
        lista="listaStatus"
        valor={status}
        aoAlterado={(valor) => setStatus(valor)}
        error={errors.status}
      />

      <FormularioInput
        type="text"
        title="Limite de saque (R$)"
        required="required"
        id="withdrawLimit"
        placeholder="Ex: 500,00"
        helper="Limite de saque permitido para o usuário (apenas admin ajusta)."
        valor={withdrawLimit}
        aoAlterado={(v) => setWithdrawLimit(v)}
        error={errors.withdrawLimit}
      />

      <FormularioInput
        type="password"
        title="Senha"
        required={!isEdit ? "required" : undefined}
        id="senhaLicenciado"
        placeholder={isEdit ? "Deixe em branco para manter a senha atual" : "Defina uma senha temporária"}
        helper={isEdit ? "Se preencher, a senha será alterada." : "O licenciado poderá alterar a senha depois no primeiro acesso."}
        valor={senha}
        aoAlterado={(valor) => setSenha(valor)}
        error={errors.senha}
      />

      <div className="form-footer">
        <FormularioButom icone="💾" disabled={loading}>
          {isEdit ? "Salvar alterações" : "Salvar Licenciado"}
        </FormularioButom>
      </div>
    </form>
  );
};

export default FormularioAddUsuarios;
